import {dataTestId} from '@hconnect/uikit'
import {HPToggleButton} from '@hconnect/uikit/src/lib2'
import {useTranslation} from 'react-i18next'

import {PLANNING_TIME_CONTROLS_WIDGET_NAMESPACE} from '../consts'
import {PlanningScope} from '../types'

interface PlanningScopeSelectorProps {
  /**
   * Current planning scope (Daily or Monthly)
   */
  scope: PlanningScope
  /**
   * Callback fired when user changes the planning scope
   */
  onChange: (newScope: PlanningScope) => void
  /**
   * Optional data-test-id attribute
   */
  ['data-test-id']?: string
}

/**
 * A toggle component for switching between Daily and Monthly planning scopes.
 * This is a presentational component that delegates navigation/state management to the parent.
 *
 * @example
 * ```tsx
 * const [scope, setScope] = useState<PlanningScope>('daily')
 *
 * const handleScopeChange = (newScope: PlanningScope) => {
 *   // Update URL params, navigate, etc.
 *   navigate({ pathname: `/planning/${newScope}`, search: `?start=${start}&end=${end}` })
 *   setScope(newScope)
 * }
 *
 * return (
 *   <PlanningScopeSelector
 *     scope={scope}
 *     onChange={handleScopeChange}
 *   />
 * )
 * ```
 */
export const PlanningScopeSelector: React.FC<PlanningScopeSelectorProps> = ({
  scope,
  onChange,
  ['data-test-id']: testId
}) => {
  const {t} = useTranslation(PLANNING_TIME_CONTROLS_WIDGET_NAMESPACE)

  return (
    <HPToggleButton
      {...dataTestId(testId ?? 'daily_monthly_toggle_button')}
      value={scope}
      backgroundMode="dark"
      color="primary"
      options={[
        {value: PlanningScope.Daily, title: t('planning.daily'), dataTestId: 'daily_button'},
        {
          value: PlanningScope.Monthly,
          title: t('planning.monthly'),
          dataTestId: 'monthly_button'
        }
      ]}
      onChange={onChange}
    />
  )
}
